import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import { EditCellRenderer } from '../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useGetRawProducts } from '../../../services/rawProd/rawProd.service';
import { RawProd } from '../../../services/rawProd/rawProd.types';
import { useChangeRawProdForSubSpec } from '../../../services/subrecipe/subrecipe.service';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import {
  H1,
  InputButtonWrapper,
  InputWrapper,
  SelectRawProdsContainer,
  StyledTableWrapper,
} from './AddOrChangeRawProd.styles';
import { AddOrChangeRawProdProps } from './AddOrChangeRawProd.types';
import ReviewChanges from './ReviewChanges/ReviewChanges';

const AddOrChangeRawProd: FC<AddOrChangeRawProdProps> = ({
  selectedOption,
  subrecipeDetailsData,
  changeRawProd,
  setChangeRawProd,
  setAddRawProdsActive,
  setOptionsTableData,
  setSelectedOption,
  setSelectedSubrecipe,
  removeSubrecipeDetails,
  refetch,
}) => {
  const { specNo } = useParams();
  const _specNo = specNo as string;
  const availableGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);
  const [selectedRawProds, setSelectedRawProds] = useState<any[]>([]);
  const [reviewChanges, setReviewChanges] = useState<Boolean>(false);

  const { data: rawProdsData, isError: rawProdsError } = useGetRawProducts(true);
  const {
    mutate: changeRawProdRequest,
    isError: changeRawProdsError,
    isLoading: isChangeRawProdLoading,
  } = useChangeRawProdForSubSpec(_specNo, selectedOption?.subSpecNo);

  const assignedRawProds = subrecipeDetailsData
    .filter(value => value.subSpecNo === selectedOption?.subSpecNo)
    .map((option) => option.rawProdNo);

  const filteredRawProds = rawProdsData?.data.filter((rawProd) => !assignedRawProds.includes(rawProd.moaRawProdNo));

  useEffect(() => {
    if (rawProdsError)
      showToast({
        ...genericErrorShowToastOptions,
        label: 'Error fetching raw products.',
        id: 'optionsRawProdsFetchError',
        toastType: ToastTypes.Error,
      });
  }, [rawProdsError]);

  useEffect(() => {
    if (changeRawProdsError)
      showToast({
        ...genericErrorShowToastOptions,
        label: 'Error updating raw products.',
        id: 'optionsRawProdsUpdateError',
        toastType: ToastTypes.Error,
      });
  }, [changeRawProdsError]);

  const selectAvailableRawProds = (selectedRows: RawProd[]) => {
    if(filteredRawProds) {
    availableGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = selectedRows.find(
        (selectedRawProd) => selectedRawProd.moaRawProdNo === node?.data?.moaRawProdNo
      );
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  }
  };

  useEffect(() => {
    if (selectedRawProds) {
      selectedGridRef?.current?.api?.selectAll();
      selectAvailableRawProds(selectedRawProds);
    }
  }, [selectedRawProds, selectedGridRef]);

  const onSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setSelectedRawProds(
      event.api.getSelectedRows().map((row) => {
        const existsInSelected = selectedRawProds.find((rawProd) => rawProd.moaRawProdNo === row.moaRawProdNo);
        return {
          ...row,
          // if changing a rawprod, set initial usage amount to the current amount
          usageAmount: changeRawProd ? selectedOption?.usageAmount || 0 : existsInSelected?.usageAmount || 0,
        };
      })
    );
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectAvailableRawProds(event.api.getSelectedRows());
  };

  const handleCellEdit = useCallback(    (event: any) => {
      let newValue = +event.newValue;
      if (newValue > 9999.9999) newValue = 9999.9999;
      if (newValue < -9999.9999) newValue = -9999.9999;
      if (Number.isNaN(newValue)) newValue = event.oldValue;
      const newSelectedRawProds = selectedRawProds.map((rawProd) => {
        if (rawProd.moaRawProdNo === event.data.moaRawProdNo) {
          return {
            ...rawProd,
            usageAmount: newValue,
          };
        } else {
          return rawProd;
        }
      });
      setSelectedRawProds(newSelectedRawProds);
    },
    [selectedRawProds]
  );

  const handleTextLinkClick = () => {
    setAddRawProdsActive(false);
    setChangeRawProd(false);
    setOptionsTableData([]);
    removeSubrecipeDetails();
  };

  const handleAddClick = () => {
  setReviewChanges(true)
  };



  const handleChangeClick = () => {
    changeRawProdRequest(
      {
        oldRawProdNo: selectedOption?.rawProdNo,
        newRawProdNo: selectedRawProds?.[0].moaRawProdNo,
        usageAmount: selectedRawProds?.[0].usageAmount,
      },
      {
        onSuccess: () => {
          handleTextLinkClick();
          showToast({
            label: 'Raw product has been updated.',
            id: 'optionsRawProdsUpdateSuccess',
            toastType: ToastTypes.Success,
          });
        },
      }
    );
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back to Spec', to: `/optionrecipes/${_specNo}`, onClick: handleTextLinkClick }} />
      <SelectRawProdsContainer>
        {changeRawProd ? (
          <>
            <H1>Change Raw Product</H1>
            <H2>Select replacement raw product for subspec</H2>
            <InputButtonWrapper>
              <InputWrapper>
                <Input
                  id='targetSubspec'
                  label='Target Subspec'
                  value={`${selectedOption?.subSpecNo?.toString()} - ${selectedOption?.optionDesc}`}
                  disabled
                />
                <Input
                  id='targetSubspec'
                  label='Current Raw Product'
                  value={`${selectedOption?.rawProdDesc} - ${selectedOption?.rawProdNo?.toString()}`}
                  disabled
                />
              </InputWrapper>
              <Button
                label='Change'
                onClick={handleChangeClick}
                disabled={selectedRawProds.length === 0}
                isLoading={isChangeRawProdLoading}
              />
            </InputButtonWrapper>
          </>
        ) : reviewChanges ?
        (
          <>
        <ReviewChanges
        _specNo={_specNo}
        selectedOption={selectedOption}
        selectedRawProds={selectedRawProds}
        setChangeRawProd={setChangeRawProd}
        setAddRawProdsActive={setAddRawProdsActive}
        setOptionsTableData={setOptionsTableData}
        setSelectedOption={setSelectedOption}
        setSelectedSubrecipe={setSelectedSubrecipe}
        removeSubrecipeDetails={removeSubrecipeDetails}
        refetch={refetch}
        setReviewChanges={setReviewChanges}
        setSelectedRawProds={setSelectedRawProds}
        />

        </>
        ):(
          <>
            <H1>Add Raw Product</H1>
            <H2>Select raw products to add to subspec</H2>
            <InputButtonWrapper>
              <Input
                id='targetSubspec'
                label='Target Subspec'
                value={`${selectedOption?.subSpecNo?.toString()} - ${selectedOption?.optionDesc}`}
                disabled
              />
              <Button
                label='Review'
                onClick={handleAddClick}
                disabled={selectedRawProds.length === 0}
              />
            </InputButtonWrapper>
          </>
        )}
        {!reviewChanges ? (
          <TableRowWrapper>
          <StyledTableWrapper>
            <Table
              ref={availableGridRef}
              showHeader={true}
              headerTitle='Available Raw Products'
              columnDefs={[
                {
                  minWidth: 70,
                  maxWidth: 70,
                  flex: 1,
                  headerName: 'Select',
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false,
                },
                { minWidth: 120, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
                { minWidth: 220, flex: 2, field: 'moaRawProdDesc', headerName: 'Description', resizable: false },
              ]}
              rowData={filteredRawProds}
              rowSelection={changeRawProd ? 'single' : 'multiple'}
              rowMultiSelectWithClick={true}
              suppressRowClickSelection={false}
              onSelectionChanged={onSelectionChangedAvailable}
              returnedRecords={filteredRawProds?.length}
              height={'100%'}
            />
          </StyledTableWrapper>
          <StyledTableWrapper>
            <Table
              ref={selectedGridRef}
              showHeader={true}
              headerTitle={changeRawProd ? 'Desired Raw Product' : 'Selected Raw Product'}
              columnDefs={[
                {
                  minWidth: 70,
                  maxWidth: 70,
                  flex: 1,
                  headerName: 'Select',
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false,
                },
                { minWidth: 120, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
                { minWidth: 220, flex: 2, field: 'moaRawProdDesc', headerName: 'Description' },
                {
                  minWidth: 120,
                  flex: 1,
                  field: 'usageAmount',
                  headerName: 'Usage Amount',
                  editable: true,
                  resizable: false,
                  cellRenderer: EditCellRenderer,
                },
              ]}
              rowData={selectedRawProds}
              onFirstDataRendered={({api}) => api.forEachNode(node => node.setSelected(true))}
              rowSelection='multiple'
              suppressRowClickSelection={true}
              onSelectionChanged={onSelectionChangedSelected}
              onCellEditRequest={handleCellEdit}
              readOnlyEdit={true}
              returnedRecords={selectedRawProds.length}
              height={'100%'}
            />
          </StyledTableWrapper>
        </TableRowWrapper>
        ) : (<></>)}

      </SelectRawProdsContainer>
    </>
  );
};

export default AddOrChangeRawProd;
